import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { LinkToArgs } from '@glint/environment-ember-loose/-private/intrinsics/link-to';
import { inject as service } from '@ember/service';
import { typeOf } from '@ember/utils';
import RouterService from '@ember/routing/router-service';
import { LinkTo } from '@ember/routing';
import cloneDeep from 'lodash.clonedeep';
import MasqService from 'cio/services/masq';
import HydraService from 'cio/services/hydra';
import VWBLinkTo from 'cio/utils/visual-workflow-builder/demo/components/link-to';
export interface CioLinkSignature {
    Element: HTMLAnchorElement;
    Args: LinkToArgs;
    Blocks: {
        default: [];
    };
}
export default class CioLink extends Component<CioLinkSignature> {
    @service
    masq: MasqService;
    @service
    router: RouterService;
    @service
    hydra: HydraService;
    get route() {
        return this.args.route ?? this.router.currentRouteName;
    }
    get models() {
        if (this.args.model != null) {
            return [
                this.args.model
            ];
        }
        return this.args.models ?? [];
    }
    get query() {
        let params1 = this.args.query;
        if (params1 == null) {
            params1 = {};
        }
        if (typeOf(params1) !== 'object') {
            return params1;
        }
        let paramsCopy1 = cloneDeep(params1 as Record<string, any>);
        // not masquerading, don't change anything
        if (!this.masq.isOn) {
            return params1;
        }
        // Generate the target href, so we can check whether it's a /env/:env_id
        // route (and as such, doesn't need a masq_account_id query param)
        // https://github.com/emberjs/ember-legacy-built-in-components/blob/fafa5e8a6373ccbb9935736b4fce5e4b972912ca/addon/components/link-to.ts#L899
        // This is how the source component generates the "href" param. We can't use
        // the "href" component property in here, because it depends on query, which
        // would cause an infinite computation loop between _query and href.
        let targetHref1;
        if (this.models.length > 0) {
            targetHref1 = this.router.urlFor(this.route, ...(this.models as any), {
                queryParams: paramsCopy1
            });
        } else {
            targetHref1 = this.router.urlFor(this.route, {
                queryParams: paramsCopy1
            });
        }
        // If the link href contains an /env/:env_id link, we don't need the
        // masq_account_id query param. We'll set it explicitly to null so that it
        // gets removed from the URL (otherwise Ember's global param handling would
        // keep it there, even if no param is set on this particular link)
        let rootUrlWithoutTrailingSlash1 = this.router.rootURL.replace(/\/$/, '');
        let envRegex1 = new RegExp(`^${rootUrlWithoutTrailingSlash1}/env/[0-9]+`);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (this.hydra.useNewWorkspacesUrls) {
            envRegex1 = new RegExp(`^${rootUrlWithoutTrailingSlash1}/workspaces/[0-9]+/`);
        }
        if (envRegex1.test(targetHref1)) {
            paramsCopy1.masqAccountID = null;
            return paramsCopy1;
        }
        // If the target href doesn't contain an environment ID and there is no
        // masq_account_id query param, we need to add that query param.
        if (paramsCopy1.masqAccountID == null) {
            paramsCopy1.masqAccountID = this.masq.masqueradedAs;
        }
        return paramsCopy1;
    }
    get LinkComponent() {
        if (this.router.currentRouteName === 'vwb-demo') {
            return VWBLinkTo;
        }
        return LinkTo;
    }
    // prettier-ignore
    static{
        template(`{{! template-lint-disable no-link-to }}<this.LinkComponent
    data-test-route={{@route}}
    @route={{this.route}}
    @models={{this.models}}
    @query={{this.query}}
    @disabled={{@disabled}}
    @current-when={{@current-when}}
    @replace={{@replace}}
    ...attributes
  >
    {{~yield~}}
  </this.LinkComponent>`, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
